import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import {
  AccessRequest,
  CreatedInvitation,
  InvitableRole,
  OrganizationInvitation,
  OrganizationMember,
  OrganizationRole,
  PublicInvitation,
} from '../models/membership.model';
import { Team, TeamChanges } from '../models/team.model';
import { UrlConstantsService } from './url-constants.service';

@Injectable({ providedIn: 'root' })
export class MembershipService {
  private role$?: Observable<OrganizationRole>;

  constructor(private http: HttpClient, private urls: UrlConstantsService) {}

  /**
   * Your role, from the server: login tokens can be older than a role change (or than roles).
   * Only shapes the UI; the API enforces roles itself. Cached for the session.
   */
  myRole(): Observable<OrganizationRole> {
    this.role$ ??= this.http.get<{ org_role?: OrganizationRole }>(this.urls.OWN_USER).pipe(
      map((me) => me.org_role ?? 'member'),
      catchError(() => of<OrganizationRole>('member')),
      shareReplay(1),
    );
    return this.role$;
  }

  getMembers(): Observable<OrganizationMember[]> {
    return this.http.get<OrganizationMember[]>(this.urls.OWN_ORGANIZATION_MEMBERS);
  }

  removeMember(uuid: string): Observable<void> {
    return this.http.delete<void>(this.urls.OWN_ORGANIZATION_MEMBER_BY_ID(uuid));
  }

  changeRole(uuid: string, role: InvitableRole): Observable<OrganizationMember> {
    return this.http.patch<OrganizationMember>(this.urls.OWN_ORGANIZATION_MEMBER_BY_ID(uuid), { role });
  }

  /** Returns the new owner and the previous owner (now admin). */
  transferOwnership(uuid: string): Observable<OrganizationMember[]> {
    return this.http.post<OrganizationMember[]>(this.urls.OWN_ORGANIZATION_TRANSFER_OWNERSHIP(uuid), {});
  }

  getInvitations(): Observable<OrganizationInvitation[]> {
    return this.http.get<OrganizationInvitation[]>(this.urls.OWN_ORGANIZATION_INVITATIONS);
  }

  /** Also used to resend: inviting the same address again replaces the old link. */
  invite(email: string, role: InvitableRole = 'member', teamUuids: string[] = []): Observable<CreatedInvitation> {
    return this.http.post<CreatedInvitation>(this.urls.OWN_ORGANIZATION_INVITATIONS, {
      email,
      role,
      team_uuids: teamUuids,
    });
  }

  /** Members get their own teams; the owner and admins get all teams. */
  getTeams(): Observable<Team[]> {
    return this.http.get<Team[]>(this.urls.OWN_ORGANIZATION_TEAMS);
  }

  createTeam(name: string): Observable<Team> {
    return this.http.post<Team>(this.urls.OWN_ORGANIZATION_TEAMS, { name });
  }

  /** member_uuids / project_uuids replace the team's lists when given. */
  updateTeam(uuid: string, changes: TeamChanges): Observable<Team> {
    return this.http.patch<Team>(this.urls.OWN_ORGANIZATION_TEAM_BY_ID(uuid), changes);
  }

  deleteTeam(uuid: string): Observable<void> {
    return this.http.delete<void>(this.urls.OWN_ORGANIZATION_TEAM_BY_ID(uuid));
  }

  revokeInvitation(uuid: string): Observable<void> {
    return this.http.delete<void>(this.urls.OWN_ORGANIZATION_INVITATION_BY_ID(uuid));
  }

  getAccessRequests(): Observable<AccessRequest[]> {
    return this.http.get<AccessRequest[]>(this.urls.OWN_ORGANIZATION_ACCESS_REQUESTS);
  }

  approveAccessRequest(uuid: string, role: InvitableRole = 'member', teamUuids: string[] = []): Observable<CreatedInvitation> {
    return this.http.post<CreatedInvitation>(this.urls.OWN_ORGANIZATION_ACCESS_REQUEST_ACTION(uuid, 'approve'), {
      role,
      team_uuids: teamUuids,
    });
  }

  dismissAccessRequest(uuid: string): Observable<void> {
    return this.http.post<void>(this.urls.OWN_ORGANIZATION_ACCESS_REQUEST_ACTION(uuid, 'dismiss'), {});
  }

  getInvitation(token: string): Observable<PublicInvitation> {
    return this.http.get<PublicInvitation>(this.urls.INVITATION(token));
  }

  requestAccess(body: { project_uuid: string; email: string; name: string; message: string; path: string }) {
    return this.http.post<{ status: string }>(this.urls.ACCESS_REQUESTS, body);
  }
}
